// /search?q= (module 10 U227; pipeline F3, B1953; SEARCH-AND-FILTER-PLAN-2026-10-04.md section 1): everything a search
// finds, each kind a tab with its count, the names first and then what Cobrain read, each passage with who was on the
// item, when, and the matter it is filed under. The words and the tab are kept in the address, so a reload or Back draws
// the same page, and a colleague who opens the same address sees only what their own rails allow (R5).
//
// At the foot, below the tabs, the reader's own mail and chats Cobrain has not read yet (module 05 R62, F4): its count,
// folded, and one press for the newest fifty, which open nothing. ?unread=open keeps it unfolded across a reload.
import { List, ListRow } from '../parts/primitives.js';
import { ActionButton } from '../parts/ActionButton.js';
import { useEffect, useState } from 'react';
import type { Store } from '../api.js';
import { SEARCH_KINDS } from './api.js';
import { FindField } from './FindField.js';
import { useFilterParams } from './params.js';
import {
  askCorpus, askNotYetRead, askRecords, failureWords, hitDetail, hitPath, KIND_LABEL, MIN_CHARS, NOT_YET_READ_LABEL, NOT_YET_READ_NOTE,
  PAGE_EACH, passageMeta, passagePath, passageTitle, READ_LABEL, READ_TAB, type Found, type Group, type Refused, type Tab, type Unread,
} from './search.js';
import './search.css';

type Names = { kind: 'idle' } | { kind: 'loading' } | { kind: 'ready'; refused: Refused | null; groups: Group[] } | { kind: 'error'; message: string };
type Read = { kind: 'idle' } | { kind: 'loading' } | { kind: 'ready'; refused: Refused | null; passages: Found[] } | { kind: 'error'; message: string };
type NotRead = { kind: 'idle' } | { kind: 'ready'; count: number; passages: Unread[] } | { kind: 'error'; message: string };

const TABS: readonly Tab[] = [...SEARCH_KINDS, READ_TAB];
const tabLabel = (t: Tab) => (t === READ_TAB ? READ_LABEL : KIND_LABEL[t]);

export function SearchPage({ stores, go }: { stores: Store[]; go: (path: string) => void }) {
  const [params, set] = useFilterParams(['q', 'tab', 'unread'] as const);
  const q = params.q.trim();
  const [names, setNames] = useState<Names>({ kind: 'idle' });
  const [read, setRead] = useState<Read>({ kind: 'idle' });
  const [notRead, setNotRead] = useState<NotRead>({ kind: 'idle' });
  const storeKey = stores.map((s) => s.tenantId).join(',');

  useEffect(() => {
    if (q.length < MIN_CHARS) { setNames({ kind: 'idle' }); setRead({ kind: 'idle' }); setNotRead({ kind: 'idle' }); return; }
    const ac = new AbortController();
    setNames({ kind: 'loading' });
    setRead({ kind: 'loading' });
    setNotRead({ kind: 'idle' });
    // The names first; what Cobrain read, and the reader's mail it has not read yet, once they are in, as the box asks them.
    askRecords(stores, q, PAGE_EACH)
      .then((a) => {
        if (ac.signal.aborted) return;
        setNames({ kind: 'ready', ...a });
        askNotYetRead(stores, q, PAGE_EACH)
          .then((n) => { if (!ac.signal.aborted) setNotRead({ kind: 'ready', ...n }); })
          .catch((e: unknown) => { if (!ac.signal.aborted) setNotRead({ kind: 'error', message: failureWords(e) }); });
        return askCorpus(stores, q).then((r) => { if (!ac.signal.aborted) setRead({ kind: 'ready', ...r }); });
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setNames((n) => (n.kind === 'loading' ? { kind: 'error', message: failureWords(e) } : n));
        setRead({ kind: 'error', message: failureWords(e) });
      });
    return () => ac.abort();
    // `stores` is read through its ids: the same stores in a new array are not a new search.
  }, [q, storeKey]);

  const refused = (names.kind === 'ready' ? names.refused : null) ?? (read.kind === 'ready' ? read.refused : null);
  const groups = names.kind === 'ready' ? names.groups : [];
  const passages = read.kind === 'ready' ? read.passages : [];
  const countOf = (t: Tab): number | null => (t === READ_TAB ? (read.kind === 'ready' ? passages.length : null) : names.kind === 'ready' ? groups.find((g) => g.kind === t)?.count ?? 0 : null);
  // The tab asked for in the address; else the first with anything in it, once the names are in.
  const asked = TABS.find((t) => t === params.tab);
  const tab: Tab = asked ?? (names.kind === 'ready' ? TABS.find((t) => t !== READ_TAB && (countOf(t) ?? 0) > 0) ?? READ_TAB : SEARCH_KINDS[0]);

  const group = tab === READ_TAB ? null : groups.find((g) => g.kind === tab) ?? null;

  return (
    <div className="search-page">
      <h1>Search</h1>
      <div className="search-page-find">
        <FindField label="Search everything" value={params.q} onChange={(v) => set({ q: v, tab: '', unread: '' })} />
      </div>
      {q.length < MIN_CHARS ? <p className="note">Type two letters or more to search companies, people, deals, meetings, tasks, chats and what Cobrain read.</p> : null}
      {q.length >= MIN_CHARS && refused ? <p className="search-refused">{refused.answer}</p> : null}
      {q.length >= MIN_CHARS && !refused ? (
        <>
          <div className="search-tabs" role="tablist" aria-label="What was found">
            {TABS.map((t) => {
              const n = countOf(t);
              return (
                <ActionButton reading key={t} type="button" role="tab" aria-selected={t === tab} className={`search-tab${t === tab ? ' on' : ''}${n === 0 ? ' none' : ''}`} onClick={() => set({ tab: t })}>
                  {tabLabel(t)} <span className="search-n">{n === null ? '…' : n.toLocaleString('en-GB')}</span>
                </ActionButton>
              );
            })}
          </div>
          <div role="tabpanel" aria-label={tabLabel(tab)}>
            {tab === READ_TAB ? (
              read.kind === 'loading' || read.kind === 'idle' ? <p className="note">Reading what Cobrain read…</p>
              : read.kind === 'error' ? <p className="note">What Cobrain read could not be searched: {read.message}</p>
              : passages.length === 0 ? <p className="note">Nothing Cobrain read mentions “{q}”.</p>
              : (
                <List>
                  {passages.map((p) => {
                    const path = passagePath(p);
                    return (
                      <ListRow key={`${p.store.tenantId}:${p.signalId}`} private={p.store.kind === 'personal'}>
                        {path ? <a href={path} className="search-t" onClick={(e) => { e.preventDefault(); go(path); }}>{passageTitle(p)}</a> : <span className="search-t">{passageTitle(p)}</span>}
                        {p.store.kind === 'personal' ? <span className="search-p">Personal</span> : null}
                        <span className="search-d">{passageMeta(p)}</span>
                        <span className="search-q">{p.passage}</span>
                      </ListRow>
                    );
                  })}
                </List>
              )
            ) : names.kind === 'loading' || names.kind === 'idle' ? <p className="note">Searching…</p>
              : names.kind === 'error' ? <p className="note">Search could not be read: {names.message}</p>
              : !group || group.count === 0 ? <p className="note">No {KIND_LABEL[tab].toLowerCase()} match “{q}”.</p>
              : (
                <>
                  <List>
                    {group.hits.map((h) => {
                      const path = hitPath(h);
                      const detail = hitDetail(h);
                      return (
                        <ListRow key={`${h.store.tenantId}:${h.id}`} private={h.store.kind === 'personal'}>
                          <a href={path} className="search-t" onClick={(e) => { e.preventDefault(); go(path); }}>{h.title || 'Untitled'}</a>
                          {h.store.kind === 'personal' ? <span className="search-p">Personal</span> : null}
                          {detail ? <span className="search-d">{detail}</span> : null}
                        </ListRow>
                      );
                    })}
                  </List>
                  {group.count > group.hits.length ? <p className="note">The first {group.hits.length.toLocaleString('en-GB')} of {group.count.toLocaleString('en-GB')}. Type more to narrow them.</p> : null}
                </>
              )}
          </div>
          {notRead.kind === 'ready' && notRead.count > 0 ? (
            <section className="search-unread-foot" aria-label={NOT_YET_READ_LABEL}>
              <h2>{NOT_YET_READ_LABEL} <span className="search-n">{notRead.count.toLocaleString('en-GB')}</span></h2>
              <ActionButton reading type="button" className="search-more" aria-expanded={params.unread === 'open'} onClick={() => set({ unread: params.unread === 'open' ? '' : 'open' })}>
                {params.unread === 'open' ? 'Hide them' : 'Show them'}
              </ActionButton>
              {params.unread === 'open' ? (
                <>
                  <p className="note">{NOT_YET_READ_NOTE}</p>
                  <List>
                    {notRead.passages.map((p) => (
                      <ListRow key={`${p.store.tenantId}:${p.signalId}`} private={p.store.kind === 'personal'}>
                        <span className="search-t">{passageTitle(p)}</span>
                        {p.store.kind === 'personal' ? <span className="search-p">Personal</span> : null}
                        <span className="search-d">{passageMeta(p)}</span>
                        <span className="search-q">{p.passage}</span>
                      </ListRow>
                    ))}
                  </List>
                  {notRead.count > notRead.passages.length ? <p className="note">The newest {notRead.passages.length.toLocaleString('en-GB')} of {notRead.count.toLocaleString('en-GB')}. Type more to narrow them.</p> : null}
                </>
              ) : null}
            </section>
          ) : notRead.kind === 'error' ? <p className="note">Your mail Cobrain has not read could not be searched: {notRead.message}</p> : null}
        </>
      ) : null}
    </div>
  );
}
